import { Box, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';

// Our Component
import CardItem from 'components/SoraWallet/Cards/CardItem';
import EstimatedValueMessage from 'components/SoraWallet/Cards/EstimatedValueMessage/EstimatedValueMessage';

// Our Utility
import { dataIsValid, dollarFormatter } from 'shared/utils';
import sentenceCapitalize from 'shared/utils/formatting/sentenceCapitalize';

// Icons
import PersonalLoan from 'assets/icons/wallet_icons/PersonalLoan.svg';

// Our Utils
import LOAN_ICON_AND_HEADING_MAPPING from 'shared/utils/walletOverview/getLoanIncomeMapping';

// Constant
import { AUTO, HELOC, MORTGAGE } from 'shared/constants';

function LoanTypeAndCostOfDebtCard({
	currentLoanDetails,
	homeDetails,
	lender
}) {
	const SoraTheme = useTheme();

	const { primary } = SoraTheme.palette;

	const { costOfDebt, tradeLineType } = currentLoanDetails;

	const getLoanInfoBasedOnTradeline =
		LOAN_ICON_AND_HEADING_MAPPING[tradeLineType];

	const { currentLoanIcon, currentLoanHeading } = getLoanInfoBasedOnTradeline;

	const isMortgageTradeline =
		tradeLineType === HELOC || tradeLineType === MORTGAGE;

	const isAutoTradeline = tradeLineType === AUTO;

	const hasHomeDetails = dataIsValid(homeDetails);

	const homeEstimatedValue =
		hasHomeDetails && homeDetails.estimatedValue
			? homeDetails.estimatedValue
			: 0;

	const isHomeAddressValid =
		dataIsValid(homeDetails) && dataIsValid(homeDetails.addressLine1);

	const homeAddressLine1 = isHomeAddressValid
		? sentenceCapitalize(currentLoanDetails.home.addressLine1)
		: null;

	const homeAddressLine2 = isHomeAddressValid
		? `${sentenceCapitalize(currentLoanDetails.home.city)}, ${
				currentLoanDetails.home.state
		  } ${currentLoanDetails.home.zipcode}`
		: null;

	return (
		<CardItem
			withoutButton
			buttonSx={{ alignSelf: 'center' }}
			sx={{
				background:
					'linear-gradient(180deg, rgba(38, 133, 193, 0.3) 0%, rgba(0, 156, 255, 0.051) 100%);'
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-evenly',
					marginBottom: 3,
					minHeight: 74
				}}
			>
				<Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
					{currentLoanHeading}
				</Typography>

				{isMortgageTradeline && homeAddressLine1 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							minHeight: 42
						}}
					>
						<Typography variant="body2">
							{homeAddressLine1}
						</Typography>

						{homeAddressLine2 && (
							<Typography variant="body2">
								{homeAddressLine2}
							</Typography>
						)}
					</Box>
				)}

				{lender && <Typography variant="body1">{lender}</Typography>}
			</Box>
			{currentLoanDetails.logoUrl && (
				<Box
					component="img"
					src={currentLoanDetails.logoUrl}
					sx={{
						userDrag: 'none',
						userSelect: 'none',
						WebkitUserSelect: 'none',
						WebKitUserDrag: 'none'
					}}
				/>
			)}

			<Box sx={{ display: 'flex' }}>
				{isMortgageTradeline ? (
					<Typography variant="subtitle1">Home Value</Typography>
				) : (
					<Typography
						variant="subtitle1"
						sx={{ display: isAutoTradeline ? 'none' : 'block' }}
					>
						Total Cost of Debt
					</Typography>
				)}
			</Box>

			{!isAutoTradeline && (
				<Typography variant="h4" sx={{ color: primary.indigo }}>
					{!isMortgageTradeline && dollarFormatter.format(costOfDebt)}
				</Typography>
			)}

			{isMortgageTradeline && (
				<EstimatedValueMessage estimatedValue={homeEstimatedValue} />
			)}

			<Box
				component="img"
				src={
					currentLoanHeading === 'Personal Loan'
						? PersonalLoan
						: currentLoanIcon
				}
				alt={currentLoanHeading}
				sx={{
					display: { xs: 'none', md: 'block' },
					position: 'absolute',
					right: '1rem',
					bottom: 0,
					width: '5.5rem !important',
					height: ' 5.5rem !important',
					marginBottom: '0.375rem !important'
				}}
			/>
		</CardItem>
	);
}

export default LoanTypeAndCostOfDebtCard;
